import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormatAsCurrencyForLocale } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { QuickWebCreditCardPaymentButton } from '@PetSureAustralia/petsure-payment/react-wrapper';
import { EmptyRecord } from '../../emptyRecord/emptyRecord';
import Loading from '../../shared/Loading/Loading';
import { InstalmentRecord } from './instalmentRecords';
import { useOutstandingPaymentTable } from './useOutstandingPaymentTable';
import { EMPTY_RECORD_MESSAGE } from '../../../constants/constants';
import './outstandingPaymentTable.scss';
import { disableNextButtonHandler } from '../../../helpers/paymentHelper';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { SLICES } from '../../../constants/slices';
import { clearFullPolicyNumber } from '../../../store/slice/petDetailsSlice';
import { scrollElementIntoView } from '../../../utils/scrollUtils';
import { COMMUNITY_CODE, MAP_RESPONSE_TYPE } from '../../../constants/payment';
import { SESSION_STORAGE_KEYS } from '../../../constants/sessionStorageKeys';

export const OutstandingPaymentTable = () => {
  const dispatch = useDispatch();
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isPayNowEnabled = cspConfiguration?.IsPayNowEnabled || false;
  const isLegacyPaymentEnabled = cspConfiguration?.IsLegacyPaymentEnabled || false;
  const isInstalmentProcessingEnabled = cspConfiguration?.IsInstalmentProcessingEnabled || false;
  const {
    validInstalmentRecords,
    errorRows,
    totalAmount,
    handleSelect,
    initInstalmentsErrors,
    createReturnUrl,
    cancelUrl,
    supplierBusinessCode,
    isQuickWebButtonDisabled,
    formUrl,
    partner,
    onBeforePaymentSubmit,
    partnerPrefix,
    handleNextButton,
    generatePaymentReferenceNumber,
    processingPolicyNumber
  } = useOutstandingPaymentTable(isInstalmentProcessingEnabled);
  const fullPolicyNumber = useSelector((state) => state?.[SLICES.PET_DETAILS]?.fullPolicyNumber);
  const panelRefs = useRef({});
  const isPartnerUsingLegacyPayment = partner === 'trupanion';

  useEffect(() => {
    if (fullPolicyNumber && panelRefs.current[fullPolicyNumber] && validInstalmentRecords) {
      scrollElementIntoView(panelRefs.current[fullPolicyNumber]);
      dispatch(clearFullPolicyNumber());
    }
  }, [fullPolicyNumber, validInstalmentRecords, dispatch]);

  const setPanelRef = (policyNumber) => (el) => {
    if (el) {
      panelRefs.current[policyNumber] = el;
    }
  };

  if (!validInstalmentRecords?.length) {
    return <EmptyRecord message={EMPTY_RECORD_MESSAGE.PAYNOW} />;
  }

  return (
    <section>
      {validInstalmentRecords?.map((transaction, petIndex) => {
        const hasSelectionInOtherTable = Object.entries(totalAmount).some(
          ([tableIndex, amount]) => Number(tableIndex) !== petIndex && amount > 0
        );
        const showPaymentControls = isPayNowEnabled && !hasSelectionInOtherTable;

        return (
          <div
            key={`${transaction.PetName}_${transaction.FullPolicyNumber}_pet`}
            ref={setPanelRef(transaction.FullPolicyNumber)}
          >
            <table className="paynow_table">
              <thead>
                <tr>
                  <th
                    colSpan="4"
                    className="pet_name"
                  >
                    {transaction.PetName}
                  </th>
                </tr>
                <tr>
                  {showPaymentControls && <th>Select</th>}
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <InstalmentRecord
                  instalmentRecord={transaction.PremiumInstallment}
                  petIndex={petIndex}
                  errorRows={errorRows}
                  handleSelect={handleSelect}
                  totalAmount={totalAmount}
                  isPaynowEnabled={showPaymentControls}
                />
              </tbody>

              {totalAmount[petIndex] > 0 && (
                <tfoot>
                  <tr className="total_section">
                    <td
                      className="totalCol"
                      colSpan="3"
                    >
                      Total
                    </td>
                    <td
                      className="amountCol"
                      colSpan="1"
                    >{`${FormatAsCurrencyForLocale(totalAmount[petIndex])}`}</td>
                  </tr>
                </tfoot>
              )}
            </table>
            {initInstalmentsErrors[transaction.PolicyNumber] && (
              <span className="init-error">{initInstalmentsErrors[transaction.PolicyNumber]}</span>
            )}
            {showPaymentControls && (
              <div
                className="button_container"
                key={`${transaction.PetName}_${transaction.FullPolicyNumber}_button`}
              >
                {isLegacyPaymentEnabled && isPartnerUsingLegacyPayment ? (
                  <button
                    type="button"
                    className="button primary"
                    onClick={() => handleNextButton(transaction)}
                    disabled={disableNextButtonHandler(petIndex, totalAmount)}
                  >
                    <span className="labelText">Next</span>
                  </button>
                ) : (
                  <div
                    className={`quickweb_button_wrapper${
                      processingPolicyNumber === transaction.PolicyNumber ? ' quickweb_button_wrapper--loading' : ''
                    }`}
                  >
                    {processingPolicyNumber === transaction.PolicyNumber && (
                      <div className="quickweb_button_overlay">
                        <Loading />
                      </div>
                    )}
                    <QuickWebCreditCardPaymentButton
                      quickWebTokenHandlerEndpoint={`${process.env.CSP_MIDDLEWARE_API}/api/payment/generateRealTimePaymentWestpacToken`}
                      formUrl={formUrl}
                      customerReferenceNumber={transaction.PolicyNumber}
                      paymentReference={generatePaymentReferenceNumber(transaction)}
                      buttonText="Next"
                      returnUrl={createReturnUrl(transaction)}
                      cancelUrl={cancelUrl(transaction)}
                      partner={partnerPrefix}
                      communityCode={COMMUNITY_CODE}
                      supplierBusinessCode={supplierBusinessCode(transaction.IsPetSureUnderwriter)}
                      principalAmount={totalAmount[petIndex]}
                      mapResponseType={MAP_RESPONSE_TYPE}
                      isButtonDisabled={isQuickWebButtonDisabled(totalAmount[petIndex]) || !!processingPolicyNumber}
                      onBeforePaymentSubmit={onBeforePaymentSubmit}
                      buttonClasses="button primary"
                      requestHeaders={{
                        'Content-Type': 'application/json',
                        Authorization: sessionStorage?.getItem(SESSION_STORAGE_KEYS.CSP_ACCESS_TOKEN)
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};
